import { API, Auth, Storage } from "aws-amplify";
import { Label, Modal, TextInput, Tooltip } from "flowbite-react";
import React, { useContext, useEffect, useRef, useState } from "react";
import AvatarEditor from "react-avatar-editor";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { FaArrowCircleRight } from "react-icons/fa";
import { FaArrowLeft, FaCalendarAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { Button2 } from "../../../common/Inputs";
import Context from "../../../Context/Context";
import EditableInput from "./EditableInput";
import "./index.css";
import EditableTextArea from "./EditableTextArea.jsx";
import { useNavigate } from "react-router-dom";
import { RiPagesFill } from "react-icons/ri";
import { htmlToPlainText, plainTextToHtml } from "../../../Utils/helpers.js";

const ProfileUpdate = ({ setClick, displayAfterClick }) => {
  const Ctx = useContext(Context);
  const UserCtx = useContext(Context).userData || {};
  const UtilCtx = useContext(Context).util;
  const { userData, setUserData } = useContext(Context);

  // Guard: don't render until userData is loaded
  const isUserLoaded = UserCtx && UserCtx.cognitoId;

  const [name, setName] = useState(UserCtx.userName || "");
  const [country] = useState(UserCtx.country || "");
  const [userType] = useState(UserCtx.userType || "");
  const [currentEmail, setCurrentEmail] = useState(UserCtx.emailId || "");
  const [showForm, setShowForm] = useState(false);
  const [about, setAbout] = useState(
    UserCtx.hasOwnProperty("about") ? htmlToPlainText(UserCtx.about) : "",
  );

  const [image, setImage] = useState(UserCtx?.imageUrl);
  const [editor, setEditor] = useState(null);
  const [scale, setScale] = useState(1);
  const [isImageSaving, setIsImageSaving] = useState(false);
  const [imageRemoved, setImageRemoved] = useState(false);

  const getInitials = (name) => {
    if (!name || typeof name !== "string") return "?";
    const names = name.split(" ").filter(Boolean);
    if (!Array.isArray(names) || names.length === 0) return "?";
    const initials = names.map((n) => n?.charAt(0)?.toUpperCase()).join("");
    return initials || "?";
  };

  const getColor = (name) => {
    if (!name) return "#888888";
    const colors = [
      "#FF5733",
      "#33FF57",
      "#5733FF",
      "#FF5733",
      "#33FF57",
      "#5733FF",
      "#FF5733",
      "#33FF57",
      "#5733FF",
      "#FF5733",
      "#33FF57",
      "#5733FF",
    ];
    const index = name.length % colors.length;
    return colors[index];
  };

  const formatDate = (epochDate) => {
    epochDate = Number(epochDate);
    if (!epochDate) return "";
    const date = new Date(epochDate);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    const formattedDay = day < 10 ? `0${day}` : day;
    const formattedMonth = month < 10 ? `0${month}` : month;
    const userLocation =
      localStorage.getItem("userLocation") || UserCtx?.location?.countryCode;
    return userLocation === "IN"
      ? `${formattedDay}/${formattedMonth}/${year}`
      : `${formattedMonth}/${formattedDay}/${year}`;
  };

  const fileInputRef = useRef(null);
  const [joiningDate] = useState(formatDate(UserCtx?.joiningDate));
  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState(UserCtx.dob || "");
  const [address, setAddress] = useState(UserCtx.address || "");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isChangePassword, setIsChangePassword] = useState(false);
  const [err, setErr] = useState("");
  const [phoneNumber, setPhoneNumber] = useState(UserCtx.phoneNumber || "");
  const [imgUrl, setImgUrl] = useState(UserCtx.imgUrl || "");
  const [phoneCode, setPhoneCode] = useState("");
  const [isPhoneChange, setIsPhoneChange] = useState(false);
  const [isPhoneCode, setIsPhoneCode] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [useEditableInput, setUseEditableInput] = useState(true);
  const [noHybridProducts, setNoHybridProducts] = useState(false);

  const [tempDob, setTempDob] = useState(
    dob ? new Date(Number(dob)).toISOString().split("T")[0] : "",
  );

  // Update state when userData loads after initial render (e.g. on page refresh)
  useEffect(() => {
    if (isUserLoaded) {
      setName(UserCtx.userName || "");
      setCurrentEmail(UserCtx.emailId || "");
      setPhoneNumber(UserCtx.phoneNumber || "");
      setImgUrl(UserCtx.imgUrl || "");
    }
  }, [isUserLoaded]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
  };

  const handleEditClick = () => {
    fileInputRef.current.click();
  };

  const handleSave = () => {
    if (editor) {
      const canvasScaled = editor.getImageScaledToCanvas();
      const croppedImage = canvasScaled.toDataURL();
      handleFileUpload(croppedImage);
    }
  };

  const handleCancel = () => {
    setImage(null);
    setEditor(null);
  };

  const handleFileUpload = async (base64File) => {
    UtilCtx.setLoader(true);
    setIsImageSaving(true);
    try {
      if (base64File.size > 5 * 1024 * 1024) {
        throw new Error("File size exceeds 5MB limit.");
      }
      const currentUser = await Auth.currentAuthenticatedUser();
      const cognitoId = currentUser.attributes?.sub || currentUser.userName;
      const blob = await fetch(base64File).then((res) => res.blob());
      const response = await Storage.put(
        `Bworkz/${cognitoId}/profile.jpg?v=` + new Date().getTime(),
        blob,
        {
          level: "public",
          region: "us-east-2",
          contentType: blob.type,
          ACL: "public-read",
        },
      );
      let imageUrl = await Storage.get(response.key);
      imageUrl = imageUrl.split("?")[0];

      const userdata = await API.put("user", `/user/profile-create-or-update`, {
        body: {
          ...userData,
          emailId: currentEmail,
          userName: name,
          phoneNumber: phoneNumber,
          dob: tempDob ? String(new Date(tempDob).getTime()) : "",
          address: address,
          imgUrl: imageUrl,
        },
      });

      const tempUser = { ...UserCtx, imgUrl: imageUrl };
      Ctx.setUserData(tempUser);
      toast.info("Profile Picture Updated");
    } catch (error) {
      console.error("Error uploading profile picture: ", error);
      toast.warn("Error updating profile picture");
    } finally {
      UtilCtx.setLoader(false);
      setIsImageSaving(false);
      setImage(null);
    }
  };

  const ifDataChanged = () => {
    const formattedDob = tempDob ? String(new Date(tempDob).getTime()) : "";
    return (
      name?.trim() !== UserCtx.userName?.trim() ||
      currentEmail?.trim() !== UserCtx.emailId?.trim() ||
      phoneNumber?.trim() !== UserCtx.phoneNumber?.trim() ||
      formattedDob !== (UserCtx.dob || "") ||
      address.trim() !== (UserCtx.address || "")
    );
  };

  // Check if contact info changed
  const isContactChanged = () => {
    return (
      currentEmail?.trim() !== UserCtx.emailId?.trim() ||
      phoneNumber?.trim() !== UserCtx.phoneNumber?.trim()
    );
  };

  // Check if profile info changed
  const isProfileChanged = () => {
    return name?.trim() !== UserCtx.userName?.trim() || imageRemoved;
  };

  // Check if password fields are filled
  const isPasswordFilled = () => {
    return (
      oldPassword.trim() !== "" ||
      password.trim() !== "" ||
      confirmPassword.trim() !== ""
    );
  };

  // Reset password fields
  const resetPasswordFields = () => {
    setOldPassword("");
    setPassword("");
    setConfirmPassword("");
    setErr("");
  };

  const onProfileUpdate = async (e) => {
    e.preventDefault();
    UtilCtx.setLoader(true);
    if (ifDataChanged()) {
      try {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(currentEmail)) {
          throw new Error("Invalid email format");
        }
        if (phoneNumber.length < 10) {
          throw new Error("Phone number must be at least 10 digits");
        }

        const formattedDob = tempDob ? String(new Date(tempDob).getTime()) : "";
        const aboutHtml = plainTextToHtml(about);
        if (aboutHtml.length > 500) {
          throw new Error("About section cannot exceed 500 characters");
        }
        const userdata = await API.put(
          "user",
          `/user/profile-create-or-update`,
          {
            body: {
              ...userData,
              emailId: currentEmail,
              userName: name,
              phoneNumber: phoneNumber,
              dob: formattedDob,
              address: address,
              imgUrl: imageRemoved ? "" : imgUrl,
            },
          },
        );
        const data = {
          ...userdata.result,
        };
        Ctx.setUserData(data);
        toast.info("Profile Updated");
        setImageRemoved(false);
      } catch (e) {
        console.log(e);
        toast.warn(e.message);
      } finally {
        UtilCtx.setLoader(false);
      }
    } else {
      toast.warn("Nothing is to be changed");
      UtilCtx.setLoader(false);
    }
  };

  const passwordValidator = () => {
    if (password.length < 8) {
      setErr("Password is too Short");
      return false;
    } else if (password !== confirmPassword) {
      setErr("Password Doesn't Match");
      return false;
    } else {
      setErr("");
      return true;
    }
  };

  const passwordVisibilityChange = () => {
    setPasswordVisible((prevState) => !prevState);
  };

  const onPasswordChange = async (e) => {
    e.preventDefault();
    UtilCtx.setLoader(true);
    if (passwordValidator()) {
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        await Auth.changePassword(currentUser, oldPassword, password);
        toast.info("Password Changed");
        setIsChangePassword(false);
      } catch (e) {
        setErr(e.message);
      } finally {
        UtilCtx.setLoader(false);
      }
    } else {
      UtilCtx.setLoader(false);
    }
  };

  const navigate = useNavigate();
  const isDarkMode = Ctx.isDarkMode ?? true;

  // Show loading state until user data is available
  if (!isUserLoaded) {
    return (
      <div
        className={`relative w-full min-h-screen flex items-center justify-center ${
          isDarkMode ? "bg-black" : "bg-[#f5f5f5]"
        }`}
        style={{ fontFamily: "Inter, sans-serif" }}>
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin h-8 w-8 border-4 border-[#FDCF08] border-t-transparent rounded-full"></div>
          <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative w-full min-h-screen p-4 md:p-8 transition-colors duration-200 ${
        isDarkMode ? "bg-black" : "bg-[#f5f5f5]"
      }`}
      style={{ fontFamily: "Inter, sans-serif" }}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageChange}
        accept="image/*"
        style={{ display: "none" }}
      />

      {image && (
        <Modal show={true} onClose={handleCancel}>
          <Modal.Header>Edit Profile Picture</Modal.Header>
          <Modal.Body>
            <div className="flex flex-col items-center">
              <AvatarEditor
                ref={(editor) => setEditor(editor)}
                image={image}
                width={250}
                height={250}
                border={50}
                borderRadius={125}
                color={[255, 255, 255, 0.6]}
                scale={scale}
                rotate={0}
              />
              <div className="mt-4 w-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Zoom
                </label>
                <input
                  type="range"
                  min="1"
                  max="2"
                  step="0.01"
                  value={scale}
                  onChange={(e) => setScale(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="ml-auto">
            <button
              className="p-1 px-4 rounded-md text-black bg-gray-200"
              onClick={handleCancel}>
              Cancel
            </button>
            <button
              className="p-1 px-4 rounded-md text-black bg-[#FDCF08] flex items-center gap-2"
              onClick={handleSave}
              disabled={isImageSaving}>
              {isImageSaving ? "Saving..." : "Save"}
            </button>
          </Modal.Footer>
        </Modal>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1
            className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            Profile
          </h1>
          <p
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"} mt-1`}>
            Update your personal details
          </p>
        </div>

        {/* Three Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-8">
          {/* Profile Column */}
          <div
            className={`rounded-xl p-6 md:p-8 ${isDarkMode ? "bg-[#1a1a1a]" : "bg-white"} flex flex-col`}>
            <div className="flex-1">
              <h2
                className={`text-lg font-bold mb-6 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                Profile
              </h2>
              <p
                className={`text-xs mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                Update your personal details.
              </p>

              {/* Avatar */}
              <div className="flex flex-col items-center mb-6">
                <div className="relative inline-block mb-4">
                  <div
                    className={`w-20 h-20 rounded-full overflow-hidden border-2 shadow-lg ${isDarkMode ? "border-[#2a2a2a]" : "border-gray-200"}`}>
                    {!imageRemoved && UserCtx.imgUrl ? (
                      <img
                        src={UserCtx.imgUrl}
                        alt="profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center"
                        style={{ backgroundColor: getColor(UserCtx.userName) }}>
                        <span className="text-xl font-bold text-white">
                          {getInitials(UserCtx.userName)}
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleEditClick}
                    className={`absolute bottom-0 right-0 rounded-full p-1.5 shadow-md transition-all ${isDarkMode ? "bg-[#2a2a2a] hover:bg-[#3a3a3a] text-[#FDCF08]" : "bg-gray-100 hover:bg-gray-200 text-[#FDCF08]"}`}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3"
                      viewBox="0 0 20 20"
                      fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                </div>
                <div className="flex gap-2 items-center justify-center">
                  {UserCtx.imgUrl && !imageRemoved && (
                    <button
                      onClick={() => setImageRemoved(true)}
                      className="text-xs text-red-500 hover:text-red-600 underline">
                      Remove Photo
                    </button>
                  )}
                </div>
              </div>

              {/* Joining Date */}
              <div
                className={`pt-4 border-t ${isDarkMode ? "border-[#2a2a2a]" : "border-gray-200"}`}>
                <p
                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                  {formatDate(UserCtx?.joiningDate)}
                </p>
                <p
                  className={`text-xs font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Joining Date
                </p>
              </div>

              {/* Full Name - Now Editable */}
              <div
                className={`mt-4 pt-4 border-t ${isDarkMode ? "border-[#2a2a2a]" : "border-gray-200"}`}>
                <label
                  className={`text-xs font-medium block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Full Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg text-xs ${isDarkMode ? "bg-[#0f0f0f] border border-[#2a2a2a] text-white" : "bg-white border border-gray-200 text-gray-900"} focus:outline-none focus:ring-1 focus:ring-[#FDCF08]`}
                  placeholder="Enter your full name"
                />
              </div>
            </div>

            {/* Save Profile Button */}
            <button
              onClick={onProfileUpdate}
              disabled={!isProfileChanged()}
              className={`w-full font-semibold py-2.5 rounded-lg transition-all text-sm mt-6 ${
                isProfileChanged()
                  ? "bg-[#FDCF08] text-black hover:bg-[#f0c000]"
                  : "bg-[#FDCF08] text-black opacity-50 cursor-not-allowed"
              }`}>
              Save Profile
            </button>
          </div>

          {/* Contact Column */}
          <div
            className={`rounded-xl p-6 md:p-8 ${isDarkMode ? "bg-[#1a1a1a]" : "bg-white"} flex flex-col`}>
            <div className="flex-1">
              <h2
                className={`text-lg font-bold mb-6 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                Contact
              </h2>
              <p
                className={`text-xs mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                Manage your contact info.
              </p>

              {/* Email */}
              <div className="mb-6">
                <label
                  className={`text-xs font-medium block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={currentEmail}
                  onChange={(e) => setCurrentEmail(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg text-xs ${isDarkMode ? "bg-[#0f0f0f] border border-[#2a2a2a] text-white" : "bg-white border border-gray-200 text-gray-900"} focus:outline-none focus:ring-1 focus:ring-[#FDCF08]`}
                />
              </div>

              {/* Phone */}
              <div className="mb-6">
                <label
                  className={`text-xs font-medium block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg text-xs ${isDarkMode ? "bg-[#0f0f0f] border border-[#2a2a2a] text-white" : "bg-white border border-gray-200 text-gray-900"} focus:outline-none focus:ring-1 focus:ring-[#FDCF08]`}
                />
              </div>

              {/* Role */}
              <div>
                <label
                  className={`text-xs font-medium block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Role
                </label>
                <input
                  type="text"
                  value={UserCtx.userType || ""}
                  disabled
                  className={`w-full px-3 py-2 rounded-lg text-xs ${isDarkMode ? "bg-[#0f0f0f] border border-[#2a2a2a] text-gray-400" : "bg-gray-50 border border-gray-200 text-gray-600"} focus:outline-none cursor-not-allowed`}
                />
              </div>
            </div>

            {/* Update Contact Button */}
            <button
              onClick={onProfileUpdate}
              disabled={!isContactChanged()}
              className={`w-full font-semibold py-2.5 rounded-lg transition-all text-sm mt-6 ${
                isContactChanged()
                  ? "bg-[#FDCF08] text-black hover:bg-[#f0c000]"
                  : "bg-[#FDCF08] text-black opacity-50 cursor-not-allowed"
              }`}>
              Update Contact
            </button>
          </div>

          {/* Security Column */}
          <div
            className={`rounded-xl p-6 md:p-8 ${isDarkMode ? "bg-[#1a1a1a]" : "bg-white"} flex flex-col mb-16 md:mb-0`}>
            <div className="flex-1">
              <h2
                className={`text-lg font-bold mb-6 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                Security
              </h2>
              <p
                className={`text-xs mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                Ensure account safety.
              </p>

              {/* Current Password */}
              <div className="mb-6">
                <label
                  className={`text-xs font-medium block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Current Password
                </label>
                <input
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder="••••••••••"
                  className={`w-full px-3 py-2 rounded-lg text-xs ${isDarkMode ? "bg-[#0f0f0f] border border-[#2a2a2a] text-white placeholder-gray-500" : "bg-white border border-gray-200 text-gray-900 placeholder-gray-400"} focus:outline-none focus:ring-1 focus:ring-[#FDCF08]`}
                />
              </div>

              {/* New Password */}
              <div className="mb-6">
                <label
                  className={`text-xs font-medium block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  New Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  className={`w-full px-3 py-2 rounded-lg text-xs ${isDarkMode ? "bg-[#0f0f0f] border border-[#2a2a2a] text-white placeholder-gray-500" : "bg-white border border-gray-200 text-gray-900 placeholder-gray-400"} focus:outline-none focus:ring-1 focus:ring-[#FDCF08]`}
                />
              </div>

              {/* Confirm Password */}
              <div>
                <label
                  className={`text-xs font-medium block mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className={`w-full px-3 py-2 rounded-lg text-xs ${isDarkMode ? "bg-[#0f0f0f] border border-[#2a2a2a] text-white placeholder-gray-500" : "bg-white border border-gray-200 text-gray-900 placeholder-gray-400"} focus:outline-none focus:ring-1 focus:ring-[#FDCF08]`}
                />
              </div>
            </div>

            {/* Change Password Button */}
            <button
              onClick={() => setIsChangePassword(true)}
              disabled={!isPasswordFilled()}
              className={`w-full font-semibold py-2.5 rounded-lg transition-all text-sm mt-6 ${
                isPasswordFilled()
                  ? "bg-[#FDCF08] text-black hover:bg-[#f0c000]"
                  : "bg-[#FDCF08] text-black opacity-50 cursor-not-allowed"
              }`}>
              Change Password
            </button>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {isChangePassword && (
        <Modal
          show={true}
          onClose={() => {
            setIsChangePassword(false);
            resetPasswordFields();
          }}>
          <Modal.Header>Change Password</Modal.Header>
          <Modal.Body>
            <form onSubmit={onPasswordChange}>
              <div className="mb-4">
                <Label htmlFor="oldPassword" value="Current Password" />
                <TextInput
                  id="oldPassword"
                  type={passwordVisible ? "text" : "password"}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  required
                />
              </div>
              <div className="mb-4">
                <Label htmlFor="newPassword" value="New Password" />
                <TextInput
                  id="newPassword"
                  type={passwordVisible ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className="mb-4">
                <Label htmlFor="confirmPassword" value="Confirm Password" />
                <TextInput
                  id="confirmPassword"
                  type={passwordVisible ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <div className="mb-4 flex items-center">
                <input
                  type="checkbox"
                  checked={passwordVisible}
                  onChange={passwordVisibilityChange}
                  className="mr-2"
                />
                <label className="text-sm">Show Password</label>
              </div>
              {err && <p className="text-red-500 text-sm mb-4">{err}</p>}
              <div className="flex justify-end gap-2">
                <Button2
                  type="button"
                  onClick={() => setIsChangePassword(false)}>
                  Cancel
                </Button2>
                <Button2 type="submit">Change Password</Button2>
              </div>
            </form>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default ProfileUpdate;
